<section class="thread" aria-labelledby="threadTitle">
  <header class="thread__head">
    <h2 class="thread__title" id="threadTitle" tabindex="-1">
      {{ replyCount() }} {{ isTopic ? (replyCount() === 1 ? 'reply' : 'replies') : (replyCount() === 1 ? 'comment' : 'comments') }}
    </h2>
    @if (comments().length) {
      <label class="thread__sort">
        <span class="sr-only">Sort {{ isTopic ? 'replies' : 'comments' }}</span>
        <select class="select select--sm thread__select" [value]="sort()" (change)="setSort($any($event.target).value)">
          @for (key of sortKeys; track key) {
            <option [value]="key" [selected]="key === sort()">{{ sortOptions[key] }}</option>
          }
        </select>
      </label>
    }
  </header>

  <p class="sr-only" role="status">{{ announcement() }}</p>

  @if (loading() && !comments().length) {
    <div class="thread__loading" aria-hidden="true">
      <div class="thread__skeleton"></div>
      <div class="thread__skeleton"></div>
    </div>
    <span class="sr-only" role="status">Loading comments</span>
  } @else if (failed()) {
    <div class="thread__error" role="alert">
      <span class="material-icons" aria-hidden="true">error_outline</span>
      <p><b>Comments couldn't load.</b></p>
      <button type="button" class="ut-btn ut-btn--secondary ut-btn--sm" (click)="loadPage(page())">Retry</button>
    </div>
  } @else if (comments().length) {
    <ol class="thread__list" [attr.aria-busy]="loading()">
      @for (comment of comments(); track comment.id; let i = $index) {
        <li
          class="thread__item"
          [id]="'comment_' + comment.id"
          tabindex="-1"
          [class.is-selected]="'' + comment.id === selectedId"
          [style.--indent]="indent(comment)">
          <div class="thread__comment">
            <!-- As a community feed card: byline with Hide / Report at the top right, then the comment, then its actions. -->
            <div class="thread__head-row">
              <div class="byline thread__byline">
                <a class="byline__author" uiSref="profile.about" [uiParams]="{ profileId: comment.user?.slug }">
                  <ut-avatar-badges class="byline__avatar" [user]="comment.user" aria-hidden="true"></ut-avatar-badges>
                  <span class="byline__name">{{ comment.user?.name }}</span>
                </a>
                <span class="byline__when">
                  @if (comment.user?.id === commentable.user?.id) {
                    <span class="badge badge--brand">Author</span>
                  } @else if (comment.user?.user_title || comment.user?.country?.name) {
                    {{ (comment.user?.user_title || comment.user?.country?.name) | strLimit: 50 }} <span aria-hidden="true">&middot;</span>&ngsp;
                  }
                  {{ comment.published_at | relativeDate }}
                  @if (comment.parent_id) {
                    <span class="sr-only">, a reply</span>
                  }
                </span>
              </div>
              @if (comment.current_user_permissions?.hide || (comment.current_user_permissions?.flag && comment.user?.id !== user?.id)) {
                <div class="thread__manage" role="group" [attr.aria-label]="'Options for the comment by ' + comment.user?.name">
                  @if (comment.current_user_permissions?.hide) {
                    <ut-community-hide-action [item]="comment"></ut-community-hide-action>
                  }
                  @if (comment.current_user_permissions?.flag && comment.user?.id !== user?.id) {
                    <ut-community-report-action [item]="comment"></ut-community-report-action>
                  }
                </div>
              }
            </div>
            <div class="thread__body">
              @if (comment.rating !== undefined && comment.rating !== null && comment.rating >= 0) {
                <ut-star-rating [rating]="comment.rating" [max]="5" [readonly]="true"></ut-star-rating>
              }
              <div class="thread__content" [innerHTML]="comment.html"></div>
              <div class="thread__actions">
                <ut-community-like-action [likeable]="comment"></ut-community-like-action>
                <ut-community-likers [likeable]="comment" [user]="user" [compact]="true"></ut-community-likers>
                @if (canComment && !isTopic) {
                  <button type="button" class="thread__action" [id]="'reply_' + i" (click)="startReply(comment, i)">
                    <span class="material-icons" aria-hidden="true">reply</span>Reply<span class="sr-only"> to {{ comment.user?.name }}</span>
                  </button>
                }
                @if (canComment && isTopic) {
                  <button type="button" class="thread__action" (click)="startQuote(comment)">
                    <span class="material-icons" aria-hidden="true">format_quote</span>Quote<span class="sr-only"> {{ comment.user?.name }}</span>
                  </button>
                }
                @if (comment.current_user_permissions?.update) {
                  <button type="button" class="thread__action" (click)="startEdit(comment)">
                    <span class="material-icons" aria-hidden="true">edit</span>Edit<span class="sr-only"> {{ comment.user?.id === user?.id ? 'your comment' : 'comment by ' + comment.user?.name }}</span>
                  </button>
                }
                @if (comment.current_user_permissions?.destroy) {
                  <button type="button" class="thread__action thread__action--danger" (click)="confirmDelete(comment)">
                    <span class="material-icons" aria-hidden="true">delete_outline</span>Delete<span class="sr-only"> comment by {{ comment.user?.name }}</span>
                  </button>
                }
              </div>
            </div>
          </div>
        </li>
      }
    </ol>
  } @else {
    <p class="thread__empty">{{ isTopic ? 'No replies yet.' : 'No comments yet.' }}{{ canComment ? ' Start the conversation.' : '' }}</p>
  }

  @if (totalPages() > 1) {
    <nav class="pager" [attr.aria-label]="(isTopic ? 'Reply' : 'Comment') + ' pages'">
      @if (page() > 1 && totalPages() > 2) {
        <button type="button" class="pager__step pager__step--end" aria-label="First page" [disabled]="loading()" (click)="goToPage(1)"><span class="material-icons" aria-hidden="true">first_page</span></button>
      }
      <button type="button" class="pager__step" aria-label="Previous page" [disabled]="page() <= 1 || loading()" (click)="goToPage(page() - 1)"><span class="material-icons" aria-hidden="true">arrow_back</span><span class="pager__label">Previous</span></button>
      <ul class="pager__pages">
        @for (n of pages(); track n) {
          <li>
            <button
              type="button"
              class="pager__page"
              [attr.aria-current]="n === page() ? 'page' : null"
              [attr.aria-label]="'Page ' + n"
              [disabled]="loading()"
              (click)="goToPage(n)">{{ n }}</button>
          </li>
        }
      </ul>
      <button type="button" class="pager__step pager__step--next" aria-label="Next page" [disabled]="page() >= totalPages() || loading()" (click)="goToPage(page() + 1)"><span class="pager__label">Next</span><span class="material-icons" aria-hidden="true">arrow_forward</span></button>
      @if (page() < totalPages() && totalPages() > 2) {
        <button type="button" class="pager__step pager__step--end" aria-label="Last page" [disabled]="loading()" (click)="goToPage(totalPages())"><span class="material-icons" aria-hidden="true">last_page</span></button>
      }
    </nav>
  }

  @if (canComment) {
    <form class="thread__composer" (submit)="$event.preventDefault(); submit()" aria-labelledby="threadComposerLabel">
      <div class="thread__composer-head">
        <span class="thread__composer-label" id="threadComposerLabel">
          @switch (draft().kind) {
            @case ('edit') { Edit your comment }
            @case ('reply') { Reply to {{ draft().target?.user?.name }} }
            @case ('quote') { Quote in a reply }
            @default { {{ composeLabel }} }
          }
        </span>
        @if (draft().kind !== 'new') {
          <button type="button" class="thread__action" (click)="cancelAndFocus()">Cancel</button>
        }
      </div>
      @if (rated) {
        <div class="thread__rating" role="group" aria-label="Your rating">
          <span class="thread__sub">Your rating</span>
          <ut-star-rating [rating]="draft().rating || 0" [max]="5" [readonly]="false" (ratingChange)="setRating($event)"></ut-star-rating>
        </div>
      }
      <ut-editor
        [id]="'threadComposerEditor'"
        [value]="draft().content"
        [textAreaLabel]="draft().kind === 'edit' ? 'Edit your comment' : 'Write your comment'"
        [mentionsRelativeToParent]="true"
        (valueChange)="setContent($event)"></ut-editor>
      <div class="thread__composer-actions">
        <span class="thread__sub">Markdown and &#64;mentions work here.</span>
        <button type="submit" class="ut-btn" [disabled]="posting()">
          {{ posting() ? 'Posting…' : (draft().kind === 'edit' ? 'Save' : 'Post') }}
        </button>
      </div>
    </form>
  } @else if (!user && !commentable.locked) {
    <p class="thread__signin">
      <a class="ut-btn ut-btn--secondary" [href]="loginUrl" target="_self">Sign in to comment</a>
    </p>
  }
</section>
